import { useCallback, useEffect, useState } from 'react';
import { GalleryCard } from '../components/GalleryCard';
import { UploadWidget } from '../components/UploadWidget';
import { ConfirmDialog, useToast } from '../components/Toast';
import { useGallery, useUpload } from '../hooks/useGallery';
import { api, type GalleryItem } from '../api/client';
import { IconFolder } from '../components/Icon';

export function GalleryRoute() {
  const gallery = useGallery();
  const upload = useUpload();
  const toast = useToast();
  const [pendingDelete, setPendingDelete] = useState<GalleryItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void gallery.refresh();
    // Depend on the stable `refresh` callback, NOT on the whole `gallery`
    // object (which is rebuilt every render and would cause an infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery.refresh]);

  const handlePick = useCallback(
    async (file: File) => {
      // Optimistic stub so the user sees their file right away.
      const objectUrl = URL.createObjectURL(file);
      const stubId = `stub-${Date.now()}`;
      const stub = {
        id: stubId,
        url: objectUrl,
        thumbnail_url: objectUrl,
        title: file.name.replace(/\.[^.]+$/, ''),
        notes: null,
        created_at: new Date().toISOString(),
      };
      gallery.optimisticAdd(stub);

      const uploaded = await upload.upload({
        file,
        title: file.name.replace(/\.[^.]+$/, ''),
      });

      if (uploaded) {
        // Replace stub with the real item.
        gallery.optimisticAdd({
          id: uploaded.id,
          url: uploaded.url,
          thumbnail_url: uploaded.thumbnail_url,
          title: uploaded.title,
          notes: uploaded.notes,
          created_at: uploaded.created_at,
        });
        // Drop the stub by reloading the list — small dataset.
        void gallery.refresh();
        URL.revokeObjectURL(objectUrl);
        toast.push('success', 'Artwork added to your gallery.');
      } else {
        // Pull the stub off the list (refresh from server).
        void gallery.refresh();
        URL.revokeObjectURL(objectUrl);
        toast.push('error', upload.errorMessage ?? 'Upload failed.');
      }
    },
    [gallery, toast, upload],
  );

  const requestDelete = useCallback((item: GalleryItem) => {
    setPendingDelete(item);
  }, []);

  const cancelDelete = useCallback(() => {
    if (deleting) return;
    setPendingDelete(null);
  }, [deleting]);

  const confirmDelete = useCallback(async () => {
    const item = pendingDelete;
    if (!item) return;
    setDeleting(true);
    const snapshot = gallery.items;
    gallery.remove(item.id);
    try {
      await api.deleteGallery(item.id);
      toast.push(
        'success',
        `Removed "${item.title ?? 'artwork'}" from your gallery.`,
        {
          actionLabel: 'Undo',
          // We don't have a true restore endpoint — Undo triggers a
          // best-effort refresh from the server (any state retained on
          // the backend is re-fetched).
          onAction: async () => {
            try {
              await gallery.refresh();
            } catch {
              /* ignore */
            }
            toast.push('info', 'Refreshed from server.');
          },
          durationMs: 6000,
        },
      );
    } catch (err) {
      // Restore on failure
      const restored = snapshot.find((existing) => existing.id === item.id);
      if (restored) gallery.restore(restored);
      toast.push('error', err instanceof Error ? err.message : 'Could not delete.');
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, gallery, toast]);

  const showUpload = (
    <UploadWidget
      uploading={upload.uploading}
      progress={upload.progress}
      onPick={handlePick}
      errorMessage={upload.errorMessage}
    />
  );

  return (
    <section
      id="panel-gallery"
      role="tabpanel"
      aria-labelledby="tab-gallery"
      className="flex flex-col gap-6"
    >
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
          Your gallery
        </h1>
        <button
          type="button"
          onClick={() => void gallery.refresh()}
          aria-label="Refresh gallery"
          className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
        >
          Refresh
        </button>
      </div>

      {gallery.items.length === 0 ? (
        <div className="flex flex-col gap-4">
          {showUpload}
          {gallery.status !== 'loading' ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-900"
            >
              <span aria-hidden="true" className="text-neutral-400 dark:text-neutral-500">
                <IconFolder size={36} aria-hidden />
              </span>
              <p className="mt-2 text-base font-semibold text-neutral-900 dark:text-neutral-50">
                No artworks yet
              </p>
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                Drag and drop an image, or click "Choose a file" above.
              </p>
              <p className="mt-3 inline-flex rounded-full bg-accent/15 px-3 py-1 text-xs font-medium text-accent-light dark:bg-accent/20 dark:text-[#a8c0ff]">
                Tip: PNG, JPG, or WebP — up to 10 MB each.
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {showUpload}
          {gallery.status === 'loading' && gallery.items.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400" aria-live="polite">
              Loading your gallery…
            </p>
          ) : null}
          {gallery.status === 'error' ? (
            <div
              role="alert"
              className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
            >
              {gallery.errorMessage ?? 'Could not load your gallery.'}
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {gallery.items.map((item) => (
              <GalleryCard
                key={item.id}
                item={item}
                onDelete={() => requestDelete(item)}
              />
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this artwork?"
        description={
          <>
            <p>
              “{pendingDelete?.title ?? 'Untitled artwork'}” will be removed
              from your gallery. You can undo this from the toast that
              appears right after.
            </p>
          </>
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Keep it"
        destructive
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
      />
    </section>
  );
}
